import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { BellIcon, ExternalLinkIcon } from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import { getNotifications, getUnreadCount, markAsRead } from '../../services/notifications';
import { Notification } from '../../utils/notificationTypes';

interface NotificationBellProps {
  /**
   * Where the dropdown opens relative to the bell.
   * - 'bottom-end' (default): drops down, right-edge-aligned to the bell. Use when the bell sits in a top-right header.
   * - 'right-start': pops out to the right of the bell, top-aligned. Use when the bell sits in a bottom-left sidebar — opening down/left would go off-screen.
   */
  placement?: 'bottom-end' | 'right-start';
}

export const NotificationBell: React.FC<NotificationBellProps> = ({ placement = 'bottom-end' }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fetch unread count on mount and poll every 60 seconds
  useEffect(() => {
    if (!user?.uid) return;

    const fetchUnreadCount = async () => {
      const count = await getUnreadCount(user.uid);
      setUnreadCount(count);
    };

    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 60000);

    return () => clearInterval(interval);
  }, [user?.uid]);

  // Fetch notifications when dropdown opens
  useEffect(() => {
    if (!isOpen || !user?.uid) return;

    const fetchNotifications = async () => {
      setLoading(true);
      try {
        const result = await getNotifications(user.uid, { limit: 5 });
        setNotifications(result.notifications);
      } catch (error) {
        console.error('Error fetching notifications:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchNotifications();
  }, [isOpen, user?.uid]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNotificationClick = async (notification: Notification) => {
    if (!user?.uid) return;

    // Mark as read
    if (!notification.isRead) {
      try {
        await markAsRead(notification.id, user.uid);
        setUnreadCount((prev) => Math.max(0, prev - 1));
        setNotifications((prev) =>
          prev.map((n) => (n.id === notification.id ? { ...n, isRead: true } : n))
        );
      } catch (error) {
        console.error('Error marking notification as read:', error);
      }
    }

    // Navigate to link
    if (notification.linkUrl) {
      setIsOpen(false);
      navigate(notification.linkUrl);
    }
  };

  const formatTimestamp = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  if (!user) return null;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-gray-300 hover:text-white focus:outline-none"
        aria-label="Notifications"
      >
        <BellIcon className="h-6 w-6" />
        {unreadCount > 0 && (
          <span className="absolute top-0.5 right-0.5 inline-flex items-center justify-center min-w-[1.1rem] h-[1.1rem] px-1 text-[10px] font-bold leading-none text-white bg-red-600 rounded-full ring-2 ring-brand-navy">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div
          className={`absolute w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50 ${
            placement === 'right-start'
              ? 'left-full bottom-0 ml-2'
              : 'right-0 mt-2'
          }`}
        >
          <div className="px-4 py-3 border-b border-gray-200">
            <h3 className="text-sm font-semibold text-gray-900">Notifications</h3>
          </div>

          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="px-4 py-8 text-center text-gray-500">
                Loading...
              </div>
            ) : notifications.length === 0 ? (
              <div className="px-4 py-8 text-center text-gray-500">
                No notifications
              </div>
            ) : (
              notifications.map((notification) => (
                <button
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0 ${
                    !notification.isRead ? 'bg-blue-50' : ''
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm ${!notification.isRead ? 'font-semibold' : 'font-medium'} text-gray-900 truncate`}>
                        {notification.title}
                      </p>
                      <p className="text-sm text-gray-600 mt-0.5 line-clamp-2">
                        {notification.message}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        {formatTimestamp(notification.createdAt)}
                      </p>
                    </div>
                    {!notification.isRead && (
                      <span className="ml-2 w-2 h-2 bg-blue-600 rounded-full flex-shrink-0 mt-2" />
                    )}
                  </div>
                </button>
              ))
            )}
          </div>

          <div className="px-4 py-3 border-t border-gray-200">
            <button
              onClick={() => {
                setIsOpen(false);
                navigate('/staff/notifications');
              }}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center"
            >
              View all notifications
              <ExternalLinkIcon className="h-4 w-4 ml-1" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
